import { useEffect } from 'react';
import {
  selectTeamLoadingStatus,
  selectViewable,
} from 'redux/slices/usersSlice.slice';
import { ensureTeamAllViewable } from 'redux/thunks/referenceData.thunk';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import useIsAuthenticated from 'redux/utils/auth/isAuthenticatedHook';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { useExponentialBackoff } from 'utilities/data/useExponentialBackoff.hook';
import { isReferenceDataFresh } from 'redux/utils/referenceDataCache.util';

const InitialDataHelper = () => {
  const viewableGuides = useAppSelector(selectViewable);
  const teamLoadingStatus = useAppSelector(selectTeamLoadingStatus);
  const teamAllViewableInFlight = useAppSelector(
    state => state.users.teamAllViewableInFlight
  );
  const teamAllViewableFetchedAtMs = useAppSelector(
    state => state.users.teamAllViewableFetchedAtMs
  );
  const isAuthenticated = useIsAuthenticated();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const dispatch = useAppDispatch();

  // Exponential backoff: 1s, 2s, 4s, 8s (4 retries total)
  const {
    scheduleRetry,
    resetAttempts,
    clearPendingRetry,
    hasReachedMaxAttempts,
  } = useExponentialBackoff({
    maxRetryAttempts: 4,
    jitterMs: 100,
  });

  // Team Members ==============================================================
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!actingUser?.hasAppAccess) return; // Don't load data if user doesn't have app access

    // Clear any pending retry timeout when conditions change
    clearPendingRetry();

    // Fresh all-viewable session cache — stop retrying.
    if (
      viewableGuides !== null &&
      isReferenceDataFresh(teamLoadingStatus, teamAllViewableFetchedAtMs)
    ) {
      resetAttempts();
      return;
    }

    // If currently loading (or background all-viewable refresh in flight), don't do anything
    if (teamAllViewableInFlight || teamLoadingStatus === 'loading') {
      return;
    }

    // Cold-load failure — exponential backoff + max attempts.
    if (teamLoadingStatus === 'failed') {
      if (hasReachedMaxAttempts()) {
        return;
      }

      scheduleRetry(() => {
        dispatch(ensureTeamAllViewable());
      });
      return;
    }

    // Missing, stale, or invalidated all-viewable cache (thunk no-ops if fresh).
    if (teamLoadingStatus === 'initial' || teamLoadingStatus === 'success') {
      // Cached team present but stamp missing/stale: refresh with backoff.
      // A failed background ensure keeps status at `success` and only toggles
      // `teamAllViewableInFlight` — an immediate re-dispatch here would spam /users/team.
      if (viewableGuides !== null) {
        if (hasReachedMaxAttempts()) {
          return;
        }
        scheduleRetry(() => {
          dispatch(ensureTeamAllViewable());
        });
        return;
      }

      resetAttempts();
      dispatch(ensureTeamAllViewable());
    }
  }, [
    dispatch,
    isAuthenticated,
    teamLoadingStatus,
    teamAllViewableInFlight,
    teamAllViewableFetchedAtMs,
    viewableGuides,
    actingUser?.hasAppAccess,
    scheduleRetry,
    resetAttempts,
    clearPendingRetry,
    hasReachedMaxAttempts,
  ]);

  return <></>;
};

export default InitialDataHelper;
